import React, { useCallback, useEffect, useState, useRef } from 'react';
import { GameEngine } from '../lib/gameEngine';
export function GameSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const [gameState, setGameState] = useState<'idle' | 'playing' | 'gameover'>('idle');
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  useEffect(() => {
    const saved = localStorage.getItem('runner_best_score');
    if (saved) setBestScore(parseInt(saved, 10));
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    // Wait a frame for layout to settle
    const timer = setTimeout(() => {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      const engine = new GameEngine({
        canvas,
        onScoreUpdate: s => setScore(s),
        onGameOver: () => {
          setGameState('gameover');
          setScore(cur => {
            setBestScore(prev => {
              const best = Math.max(prev, cur);
              localStorage.setItem('runner_best_score', best.toString());
              return best;
            });
            return cur;
          });
        }
      });
      engineRef.current = engine;
    }, 50);
    return () => {
      clearTimeout(timer);
      if (engineRef.current) {
        engineRef.current.destroy();
        engineRef.current = null;
      }
    };
  }, []);
  const handleJump = useCallback(() => {
    if (gameState === 'idle') setGameState('playing');
    engineRef.current?.jump();
  }, [gameState]);
  const handleRestart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setGameState('playing');
    setScore(0);
    engineRef.current?.start();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        handleJump();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleJump]);
  return <div className="mt-24" data-id="element-179">
      <div className="relative w-full overflow-hidden" data-id="element-180">
        <div ref={containerRef} className="w-full h-[300px] sm:h-[400px] cursor-pointer touch-none relative" onMouseDown={e => {
        e.preventDefault();
        handleJump();
      }} onTouchStart={e => {
        e.preventDefault();
        handleJump();
      }} data-id="element-181">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full block" data-id="element-182" />

          {gameState === 'idle' && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-183">
              <div className="bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-3 rounded-full border border-primary/30 animate-pulse" data-id="element-184">
                <span className="font-mono text-xs text-primary uppercase tracking-widest" data-id="element-185">
                  Click / Space to Jump
                </span>
              </div>
            </div>}

          {gameState === 'gameover' && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-186">
              <button onClick={handleRestart} className="pointer-events-auto bg-transparent border border-primary text-primary px-8 py-3 font-mono text-sm uppercase tracking-widest hover:bg-primary/10 hover:shadow-[0_0_20px_rgba(198,255,0,0.35)] transition-all duration-300" data-id="element-187">
                Restart
              </button>
            </div>}
        </div>

        <div className="h-12 flex items-center justify-end px-6 gap-8" data-id="element-188">
          <div className="flex items-baseline gap-3" data-id="element-189">
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest" data-id="element-190">
              Score
            </span>
            <span className="font-mono text-lg text-foreground" data-id="element-191">
              {score.toString().padStart(5, '0')}
            </span>
          </div>
          <div className="flex items-baseline gap-3" data-id="element-192">
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest" data-id="element-193">
              Best
            </span>
            <span className="font-mono text-lg text-primary drop-shadow-[0_0_8px_rgba(198,255,0,0.5)]" data-id="element-194">
              {bestScore.toString().padStart(5, '0')}
            </span>
          </div>
        </div>
      </div>
    </div>;
}